import { useEffect, useState } from "react";
import {
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Shield,
  Clock,
  Zap,
  Loader2,
} from "lucide-react";
import { toast } from "react-toastify";
import { booking_request } from "../../constants/apis";

const benefits = [
  { icon: Clock, text: "Response within 24 hours" },
  { icon: Zap, text: "Competitive pricing guaranteed" },
  { icon: Shield, text: "Quality-assured suppliers" },
];

export default function ContactSection() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    requestType: "",
    vertical: "",
    message: "",
  });

  useEffect(() => {
    const hash = window.location.hash;

    if (!hash) return;

    const el = document.querySelector<HTMLDivElement>(hash);

    if (el) {
      el.style.scrollMarginTop = "-60px";

      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.", {
        position: "top-center",
      });
      return;
    }

    const payload = {
      name: form.name,
      email: form.email,
      company: form.company,
      request_type: form.requestType,
      vertical: form.vertical,
      message: form.message,
    };

    setLoading(true);
    try {
      const res = await fetch(booking_request, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Network request is not ok");

      const data = await res.json();

      if (data.status !== "success") {
        throw new Error(data.message || "Request failed");
      }

      toast.success("Quote request sent! We'll get back to you shortly.", {
        position: "top-center",
        autoClose: 15000,
      });
      setForm({
        name: "",
        email: "",
        company: "",
        requestType: "",
        vertical: "",
        message: "",
      });
    } catch (err) {
      console.error("Error submitting quote:", err);
      toast.error(
        "Something went wrong. Please try again or email us directly.",
        {
          position: "top-center",
        },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-10 md:py-16 relative overflow-hidden">
      <div className="container px-4">
        <div className="text-center mb-14">
          <p className="text-sm font-semibold text-primary mb-3 tracking-wide uppercase">
            Get in touch
          </p>
          <h2 className="text-3xl md:text-[2.75rem] font-display font-bold text-foreground leading-tight tracking-tight">
            Ready for smarter{" "}
            <span className="text-gradient">procurement?</span>
          </h2>
          <p className="max-w-lg mx-auto text-sm text-muted-foreground leading-relaxed mt-4">
            Tell us what you need — we'll get back with pricing, supplier
            options, and logistics.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="bg-card rounded-2xl border border-border p-8 md:p-10 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Work Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm transition-all"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Acme Corp"
                    className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Type of Request
                  </label>
                  <select
                    name="requestType"
                    value={form.requestType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm transition-all"
                  >
                    <option value="">Select type</option>
                    <option>Procurement</option>
                    <option>Embedded finance</option>
                    <option>Logistics</option>
                    <option>Trade automation tool</option>
                    <option>Debt Investors</option>
                    <option>Partnership</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Vertical
                </label>
                <select
                  name="vertical"
                  value={form.vertical}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm transition-all"
                >
                  <option value="">Select vertical</option>
                  <option>Petrochemicals</option>
                  <option>Agro Commodities</option>
                  <option>Minerals & Ores</option>
                  <option>Chemicals</option>
                  <option>Wood & Timber</option>
                  <option>Not Applicable</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  What do you need? *
                </label>
                <textarea
                  rows={4}
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Product, quantity, destination..."
                  className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-sm resize-none transition-all"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-bold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Submit Request <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mt-8">
            {benefits.map((b) => (
              <div
                key={b.text}
                className="flex items-center gap-2 text-sm text-muted-foreground"
              >
                <b.icon className="w-4 h-4 text-primary" />
                <span>{b.text}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-6 mt-6 pt-6 border-t border-border">
            <a
              href="mailto:manju@taram.io"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <Mail className="w-4 h-4" /> manju@taram.io
            </a>
            <a
              href="mailto:vinju@taram.io"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <Mail className="w-4 h-4" /> vinju@taram.io
            </a>
          </div>
        </div>

        <div id="offices" className="max-w-4xl mx-auto mt-10 scroll-mt-24">
          <p className="text-center text-xs font-semibold text-primary uppercase tracking-wide mb-5">
            Our Global Offices
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                flag: <span className="fi fi-us"></span>,
                region: "America",
                country: "USA",
                address: "7 Alaris Aisle, Irvine,\nCalifornia - 92614",
                phone: "+1 316 7680502",
                tel: "+13167680502",
                accent: "from-blue-500/10 to-red-500/10",
              },
              {
                flag: <span className="fi fi-cz"></span>,
                region: "Europe",
                country: "Czech Republic",
                address: "23 Rohanske Nabrezi, Karlin,\nPrague 18600",
                // phone: "+420 775225229",
                // tel: "+420775225229",
                accent: "from-blue-500/10 to-yellow-500/10",
              },
              {
                flag: <span className="fi fi-in"></span>,
                region: "Asia",
                country: "India",
                address:
                  "18, 3B Saibaba Complex,\nAvinashi Road, Coimbatore,\nTamil Nadu",
                phone: "+91 9842370036",
                tel: "+919842370036",
                accent: "from-orange-500/10 to-green-500/10",
              },
            ].map((office) => (
              <div
                key={office.region}
                className={`relative group rounded-2xl border border-border p-5 bg-gradient-to-br ${office.accent} hover:shadow-md transition-all duration-300`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl leading-none">{office.flag}</span>
                  <div>
                    <p className="text-sm font-bold text-foreground">
                      {office.region}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {office.country}
                    </p>
                  </div>
                </div>
                <div className="space-y-2 text-[13px] text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary/60" />
                    <span className="whitespace-pre-line">
                      {office.address}
                    </span>
                  </div>
                  {office.phone && office.tel && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 shrink-0 text-primary/60" />
                      <a
                        href={`tel:${office.tel}`}
                        className="hover:text-primary transition-colors"
                      >
                        {office.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
